import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import { LockedUi } from "components/LockedUi/LockedUi";
import useTransactionTableColumn from "hooks/useTransactionTableColumn";
import { ChangeEvent, Fragment, useMemo, memo } from "react";
import { useSelector } from "react-redux";
import {
  Row,
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
} from "react-table-8.10.7";
import { RootState } from "store/store";
import type { Transactions as TransactionsType } from "types/Models/books";
import { ModalProps } from "types/utils/modal";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { transactionColumn } from "components/Transaction/TransactionsTable";
import {
  TRANSACTION_COLUMNS,
  FIXED_TRANSACTION_COLUMNS,
  TRANSACTION_COLUMNS_OPTIONS,
} from "constants/transaction";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import ToolTip from "components/design/toolTip";
import { PlusIcon } from "components/icons/PlusIcon";
import { TransactionTableColumns } from "components/Transaction/TransactionsTable";
import { SplitTransaction } from "components/Transaction/SplitTransaction/SplitTransaction";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { MerchantColumn } from "./MerchantColumn";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

const columnHelper = createColumnHelper<TransactionsType>();

const CustomTransactionTableHandler = ({
  values,
  updateFilter,
}: {
  values: TransactionTableColumns;
  updateFilter: <S extends keyof TransactionTableColumns>(
    name: S,
    newValue: TransactionTableColumns[S]
  ) => void;
}) => {
  const MODAL_FIXED_COLUMNS = [
    ...FIXED_TRANSACTION_COLUMNS,
    TRANSACTION_COLUMNS.DESCRIPTION,
    TRANSACTION_COLUMNS.FROM,
    TRANSACTION_COLUMNS.VENDOR,
  ];

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;

    if (checked) {
      updateFilter("COLUMNS", [
        ...values.COLUMNS,
        name as keyof typeof TRANSACTION_COLUMNS,
      ]);
      return;
    }

    updateFilter(
      "COLUMNS",
      values.COLUMNS.filter((column) => column !== name)
    );
  };

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <span>
          <ToolTip text="Add columns" side="bottom">
            <span>
              <Button size="small">
                <PlusIcon size="12" />
              </Button>
            </span>
          </ToolTip>
        </span>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content>
          {TRANSACTION_COLUMNS_OPTIONS.map((option) => {
            const { label, value } = option;

            return (
              <Dropdown.Item key={value} onSelect={(e) => e.preventDefault()}>
                <Checkbox
                  disabled={MODAL_FIXED_COLUMNS.includes(value)}
                  label={
                    <div className="t-lowercase first-letter:t-uppercase">
                      {label}
                    </div>
                  }
                  name={value}
                  checked={values.COLUMNS.includes(value)}
                  onChange={onChange}
                />
              </Dropdown.Item>
            );
          })}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

export const TransactionsWithoutVendorModal = ({
  isOpen,
  close,
  transactions,
  pageNum,
  perPage,
  totalCount,
  totalPages,
  onPageNumChange,
  onVendorUpdate,
  groupId,
  entityId,
}: ModalProps & {
  transactions: TransactionsType[];
  pageNum: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
  onPageNumChange: (page: number) => void;
  onVendorUpdate: (transactionId: string, isDeleted: boolean) => void;
  groupId: string;
  entityId: string;
}) => {
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.EXPENSE
  );

  if (!isOpen) return null;

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="xxl" useCustomOverlay>
        <Modal.Header>
          <div>
            <Modal.Title>Assign Vendors to Transactions</Modal.Title>
            <Modal.Subtitle>
              All changes are saved automatically while you proceed.
            </Modal.Subtitle>
          </div>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-h-full t-overflow-hidden">
          <div className="t-flex t-flex-col t-gap-4">
            <div className="t-flex t-justify-end">
              <PaginationHandler
                page_num={pageNum}
                per_page={perPage}
                total_count={totalCount}
                total_pages={totalPages}
                onPageNumChange={onPageNumChange}
              />
            </div>
            <div className="t-flex-1">
              <TransactionsTable
                transactions={transactions}
                onVendorUpdate={onVendorUpdate}
                groupId={groupId}
                entityId={entityId}
                readOnly={!hasWritePermission}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.EXPENSE}
            blockedUi={<LockedButton text="Finish" />}
          >
            <Modal.RawClose asChild>
              <Button type="button" customType="primary">
                Finish
              </Button>
            </Modal.RawClose>
          </LockedUi>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

const TransactionsTable = memo(
  ({
    transactions,
    onVendorUpdate,
    entityId,
    groupId,
    readOnly,
  }: {
    transactions: TransactionsType[];
    onVendorUpdate: (transactionId: string, isDeleted: boolean) => void;
    groupId: string;
    entityId: string;
    readOnly?: boolean;
  }) => {
    const { showSplitTransactionModal } = useSelector(
      (state: RootState) => state.splitTransaction
    );

    const MODAL_DEFAULT_COLUMNS = [
      ...FIXED_TRANSACTION_COLUMNS,
      TRANSACTION_COLUMNS.VENDOR,
      TRANSACTION_COLUMNS.FROM,
      TRANSACTION_COLUMNS.DESCRIPTION,
    ];

    const { columnVisibility, updateFilter, values } =
      useTransactionTableColumn({
        txnAccountType: "TRANSACTIONS_WITHOUT_VENDOR_MODAL" as any,
        defaultColumns: MODAL_DEFAULT_COLUMNS,
      });

    const hasWritePermission = !readOnly;

    const columns = useMemo(
      () => [
        transactionColumn.date(),
        transactionColumn.from(),
        // Custom merchant column with optimistic updates
        columnHelper.accessor("transaction.merchant", {
          id: TRANSACTION_COLUMNS.VENDOR,
          size: 181,
          header: (header) => (
            <HeadCellContent
              title="To/From"
              sort={{
                nextSortOrder: header.column.getNextSortingOrder(),
                isSorted: header.column.getIsSorted(),
                sortingHandler: header.column.getToggleSortingHandler(),
              }}
            />
          ),
          cell: (info) => (
            <MerchantColumn
              info={info}
              onVendorUpdate={onVendorUpdate}
              entityId={entityId}
              groupId={groupId}
              isDisabled={readOnly}
            />
          ),
        }),
        transactionColumn.description(),
        transactionColumn.amount(),
        transactionColumn.category({ isDisabled: !!readOnly }),
        transactionColumn.invoice({ hasWritePermission }),
        transactionColumn.daily_entry(),
        transactionColumn.memo({ hasWritePermission }),
        transactionColumn.comments({ hasWritePermission }),
      ],
      [onVendorUpdate, entityId, groupId, hasWritePermission]
    );

    const table = useReactTable({
      data: transactions,
      columns,
      getCoreRowModel: getCoreRowModel(),
      enableRowSelection: false,
      state: {
        columnVisibility: columnVisibility,
        columnPinning: {
          right: [
            TRANSACTION_COLUMNS.INVOICE,
            TRANSACTION_COLUMNS.MEMO,
            TRANSACTION_COLUMNS.COMMENTS,
          ],
        },
      },
      getRowId: (row) => row.transaction.uuid,
    });

    const firstPinnedColumn = table
      .getAllColumns()
      .find((c) => c.getIsVisible() && c.getIsPinned());

    const actionIconColumn = [
      TRANSACTION_COLUMNS.MEMO as string,
      TRANSACTION_COLUMNS.COMMENTS as string,
      TRANSACTION_COLUMNS.INVOICE as string,
    ];

    const DraggableRow = (
      props: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLTableRowElement>,
        HTMLTableRowElement
      > & { row: Row<TransactionsType> }
    ) => {
      const { transaction } = props.row.original;

      return (
        <tr
          {...props}
          className={classNames(
            "hover:t-bg-surface-lighter-grey t-border-neutral-0 t-border-b t-border-0 t-text-body-sm t-border-solid",
            {
              [props.className || ""]: props.className,
            }
          )}
        />
      );
    };

    return (
      <>
        <div className="t-relative">
          <div className="t-absolute t-top-0 t-right-0 t-z-filter-head t-mr-1 t-mt-1 t-bg-surface">
            <CustomTransactionTableHandler
              updateFilter={updateFilter}
              values={values}
            />
          </div>
          <Table.Container
            className="t-overflow-auto scrollbar-visible t-max-h-[60vh] t-w-full t-pb-8 t-rounded-tl-lg t-rounded-tr-lg"
            sticky
          >
            <Table.Content>
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => {
                  return (
                    <Table.HeadRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const styles = getCommonPinningStyles(header.column);
                        const isPinnedColumn = header.column.getIsPinned();

                        return (
                          <Table.HeadCell
                            className={classNames("t-text-subtext-sm t-group", {
                              "!t-bg-surface-lighter-grey": isPinnedColumn,
                              "t-drop-shadow-table-left-pin":
                                firstPinnedColumn?.id === header.id,
                            })}
                            size={
                              actionIconColumn.includes(header.id)
                                ? "small"
                                : "regular"
                            }
                            key={header.id}
                            style={{
                              minWidth: header.getSize(),
                              marginTop: "-4px",
                              ...styles,
                            }}
                            isUpperCase={false}
                          >
                            <span className="t-flex t-gap-1 t-items-center">
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </span>
                          </Table.HeadCell>
                        );
                      })}
                    </Table.HeadRow>
                  );
                })}
              </Table.Head>
              <Table.Body>
                {table.getRowModel().rows.map((row) => {
                  return (
                    <Fragment key={row.id}>
                      <DraggableRow
                        row={row}
                        className="t-group !t-border-none"
                      >
                        {row.getVisibleCells().map((cell) => {
                          const styles = getCommonPinningStyles(cell.column);
                          const isPinnedColumn = cell.column.getIsPinned();

                          return (
                            <Table.Cell
                              id={cell.id}
                              key={cell.id}
                              style={{
                                ...styles,
                                minWidth: cell.column.getSize(),
                                maxWidth: cell.column.getSize(),
                              }}
                              size={
                                actionIconColumn.includes(cell.column.id)
                                  ? "small"
                                  : "regular"
                              }
                              className={classNames(
                                "t-border-solid t-border-neutral-0 t-border-b t-border-0",
                                {
                                  "group-hover:!t-bg-surface-lighter-grey":
                                    isPinnedColumn,
                                  "t-drop-shadow-table-left-pin":
                                    firstPinnedColumn?.id === cell.column.id,
                                }
                              )}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Table.Cell>
                          );
                        })}
                      </DraggableRow>
                    </Fragment>
                  );
                })}
              </Table.Body>
            </Table.Content>
          </Table.Container>
        </div>

        {showSplitTransactionModal && (
          <SplitTransaction isOpen={showSplitTransactionModal} />
        )}
      </>
    );
  }
);
